import { useEffect, useState, useCallback, useRef } from 'react';
import { CustodianSearchParams } from '../../Models/custodian/custodianSearchParams';
import { rowsPerPage } from '../../settings/appConfig';
import useHttp from '../http/useHttp';

export default function useFetchCustodiansSearchResult() {
  const { postDataResponse, postData } = useHttp();
  const [isLoading, setIsLoading] = useState(true);
  const totalCountRef = useRef(0);

  const [searchParams, setSearchParams] = useState<CustodianSearchParams>({
    pageSize: rowsPerPage,
    pageNo: 0
  });

  useEffect(() => {
    totalCountRef.current = postDataResponse?.data?.count ?? 0;
  }, [postDataResponse]);

  const executeSearch = useCallback(
    async (params: CustodianSearchParams, resetCount = false) => {
      try {
        setIsLoading(true);

        if (resetCount) {
          totalCountRef.current = 0;
        }

        await postData('entity/search', params);
        setSearchParams({ ...params });
      } catch (error) {
        console.log(`Exception while doing something: ${error}`);
      } finally {
        setIsLoading(false);
      }
    },
    [postData]
  );

  useEffect(() => {
    executeSearch(
      {
        pageNo: 0,
        pageSize: rowsPerPage
      },
      true
    );
  }, [executeSearch]);

  const pageChanged = useCallback(
    async (pageNumber: number) => {
      await executeSearch({
        ...searchParams,
        pageNo: pageNumber,
        pageSize: rowsPerPage
      });
    },
    [executeSearch, searchParams]
  );

  const searchChanged = useCallback(
    async (searchText?: string, stateId?: string) => {
      await executeSearch(
        {
          searchText,
          type: stateId,
          pageNo: 0,
          pageSize: rowsPerPage
        },
        true
      );
    },
    [executeSearch]
  );

  return {
    data: postDataResponse?.data ?? {
      count: totalCountRef.current,
      custodians: []
    },
    pageChanged,
    searchChanged,
    isLoading
  };
}
